import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AdvancedFilterService } from '../../services/advanced-filter/advanced-filter.service';
import { AdvancedFilter } from '@app/sharedModule/models/advanced-filter.model';
import { AdvancedFiltersActions } from '../actions';
import { OrderService } from '@app/services/order/order.service';

@Injectable()
export class AdvancedFiltersEffects {
  constructor(
    private store: Store,
    private actions$: Actions,
    private advancedFilterService: AdvancedFilterService,
    private orderService: OrderService,
  ) {}

  refreshAdvancedFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedFiltersActions.refreshAdvancedFilters),
      switchMap(() => {
        return this.advancedFilterService.getAdvancedFilters().pipe(
          map((advancedFilters: AdvancedFilter[]) => {
            return AdvancedFiltersActions.refreshAdvancedFiltersSuccess({
              advancedFilters,
            });
          }),
          catchError(() => {
            return of(AdvancedFiltersActions.refreshAdvancedFiltersFailure());
          }),
        );
      }),
    ),
  );

  loadCountReadyOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedFiltersActions.loadCountReadyOrders),
      switchMap(() => {
        return this.orderService.countReadyOrders().pipe(
          map((countReadyOrders: number) => {
            return AdvancedFiltersActions.loadCountReadyOrdersSuccess({
              countReadyOrders,
            });
          }),
          catchError(() => {
            return of(AdvancedFiltersActions.loadCountReadyOrdersFailure());
          }),
        );
      }),
    ),
  );

  loadCountHoldOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedFiltersActions.loadCountHoldOrders),
      switchMap(() => {
        return this.orderService.countHoldOrders().pipe(
          map((countHoldOrders: number) => {
            return AdvancedFiltersActions.loadCountHoldOrdersSuccess({
              countHoldOrders,
            });
          }),
          catchError(() => {
            return of(AdvancedFiltersActions.loadCountHoldOrdersFailure());
          }),
        );
      }),
    ),
  );

  loadCountScheduleOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedFiltersActions.loadCountScheduleOrders),
      switchMap(() => {
        return this.orderService.countScheduledOrders().pipe(
          map((countScheduleOrders: number) => {
            return AdvancedFiltersActions.loadCountScheduleOrdersSuccess({
              countScheduleOrders,
            });
          }),
          catchError(() => {
            return of(AdvancedFiltersActions.loadCountScheduleOrdersFailure());
          }),
        );
      }),
    ),
  );

  deleteAdvancedFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedFiltersActions.deleteAdvancedFilter),
      switchMap(({ filterId }) => {
        return this.advancedFilterService.removeAdvancedFilter(filterId).pipe(
          map(() => {
            return AdvancedFiltersActions.deleteAdvancedFilterSuccess({
              filterId,
            });
          }),
          catchError(() => {
            return of(AdvancedFiltersActions.deleteAdvancedFilterFailure());
          }),
        );
      }),
    ),
  );

  deleteAdvancedFilterSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedFiltersActions.deleteAdvancedFilterSuccess),
      map(() => AdvancedFiltersActions.refreshAdvancedFilters()),
    ),
  );

  addAdvancedFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedFiltersActions.addAdvancedFilter),
      switchMap(({ filterRequest }) => {
        return this.advancedFilterService.addAdvancedFilter(filterRequest).pipe(
          map((filter: AdvancedFilter) => {
            return AdvancedFiltersActions.addAdvancedFilterSuccess({ filter });
          }),
          catchError(() => {
            return of(AdvancedFiltersActions.addAdvancedFilterFailure());
          }),
        );
      }),
    ),
  );
}
